import './ActivityPage.scss';

import * as React from 'react';

import { ActivityStage } from '@showbie-socrative/socrative-utils';

import { translate } from '~l10n/client';
import { AsyncDialogContext } from '~shared/context/AsyncDialogContext';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { Direction } from '~shared/utils/sort';
import { ActivityLiveEvent } from '~teacher/components/ActivityResult/ActivityLiveEvent';
import { ActivityResultFinished } from '~teacher/components/ActivityResult/ActivityResultFinished';
import { ActivityResultLive } from '~teacher/components/ActivityResult/ActivityResultLive';
import { ActivityResultProvider } from '~teacher/components/ActivityResult/ActivityResultProvider';
import { StudentSortProvider } from '~teacher/components/ActivityResult/context/StudentSortContext';
import { useLiveListenerGrace } from '~teacher/components/ActivityResult/hooks/useLiveListenerGrace';
import { InviteStudentDrawerContext } from '~teacher/components/ActivityResult/modals/InviteStudentDrawer/InviteStudentDrawerContext';
import { ActivityResultError } from '~teacher/components/ActivityResult/States/ActivityResultError';
import { ActivityResultLoading } from '~teacher/components/ActivityResult/States/ActivityResultLoading';
import { ActivityResultUnavailable } from '~teacher/components/ActivityResult/States/ActivityResultUnavailable';
import {
  QuizResultsHeaderFinished,
  QuizResultsHeaderInProgress,
} from '~teacher/components/QuizResultsHeader';
import { QuizResultsHeaderSansControls } from '~teacher/components/QuizResultsHeader/QuizResultsHeaderSansControls';
import { ReportAnalyticsProvider } from '~teacher/components/QuizResultsHeader/ReportAnalyticsContext';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import {
  useActivityByInstanceId,
  useIsQuickQuestion,
  useQuiz,
} from '~teacher/hook';

import { isQuizNotFoundError } from '../../api/isQuizNotFoundError';
import { personalQuizUrl as quizUrl } from '../../api/urls';

interface ActivityPageProps {
  activityInstanceId: number;
  showInsights?: boolean;
}

export function ActivityPage({
  activityInstanceId,
  showInsights = false,
}: ActivityPageProps) {
  const teacherApplicationContext = useTeacherApplicationContext();
  const socrativeRouter = useSocrativeRouter();

  const activityResult = useActivityByInstanceId({
    activityInstanceId,
    pubnub: window.pubnub,
  });

  const activity = activityResult.data?.activity;

  const isQuickQuestion = useIsQuickQuestion(activity?.activityType);

  const quizResult = useQuiz(activity?.quizId, quizUrl);

  // Keep the live PubNub listener subscribed briefly after the activity
  // finishes so a student's teacher-finish flush (saved within the server's
  // end-of-activity grace) still lands in the results cache instead of being
  // missed because the listener was torn down the instant it finished.
  const liveListenerActive = useLiveListenerGrace(
    activity?.activityStage === ActivityStage.InProgress
  );

  React.useEffect(() => {
    document.body.classList.add('activity-page');
    return () => {
      document.body.classList.remove('activity-page');
    };
  }, []);

  const handleFinishActivity = () => {
    socrativeRouter.routeToResults(activityInstanceId);
  };

  if (quizResult.isError || activityResult.isError) {
    // A deleted/inaccessible source library gets a tailored message; any other
    // failure falls through to the generic error state.
    return isQuizNotFoundError(quizResult.error) ||
      isQuizNotFoundError(activityResult.error) ? (
      <ActivityResultUnavailable />
    ) : (
      <ActivityResultError />
    );
  }

  if (activityResult.isInitialLoading || quizResult.isInitialLoading) {
    return <ActivityResultLoading />;
  }

  if (activityResult && quizResult) {
    const { responses, students, room, questionScores } = activityResult.data;
    const { quiz, quizAnswer, quizQuestion } = quizResult.data;
    const quizTitle = isQuickQuestion ? translate('Quick Question') : quiz.name;

    const isFinishedActivity =
      activity.activityStage === ActivityStage.Finished;

    return (
      <ActivityResultProvider questionScores={questionScores}>
        <StudentSortProvider
          students={Object.values(students)}
          initialSortDirection={Direction.Ascending}
          initialSortKey={'name'}
        >
          <InviteStudentDrawerContext>
            <AsyncDialogContext>
              <div className="font-sans">
                <div className="sticky left-0 -mr-8 w-screen legacy-container">
                  <ReportAnalyticsProvider
                    analyticsData={{
                      activity,
                      studentCount: Object.keys(students).length,
                      questionCount: Object.keys(quizQuestion).length,
                    }}
                  >
                    {teacherApplicationContext ===
                    TeacherApplicationContext.ShowbieAssessments ? (
                      <QuizResultsHeaderSansControls
                        title={quizTitle}
                        quizStandard={quiz.standard?.name}
                        startDate={activity.startTime}
                        endDate={activity.endTime}
                        activityType={activity.activityType}
                        activityInstanceId={activity.id}
                        activitySettings={activity.settings}
                        libraryName={activity.workspaceName ?? undefined}
                      />
                    ) : isFinishedActivity ? (
                      <QuizResultsHeaderFinished
                        title={quizTitle}
                        quizStandard={quiz.standard?.name}
                        startDate={activity.startTime}
                        endDate={activity.endTime}
                        activityType={activity.activityType}
                        activityInstanceId={activity.id}
                        activitySettings={activity.settings}
                        libraryName={activity.workspaceName ?? undefined}
                        workspaceId={activity.workspaceId ?? undefined}
                      />
                    ) : (
                      <QuizResultsHeaderInProgress
                        title={quizTitle}
                        quizStandard={quiz.standard?.name}
                        startDate={activity.startTime}
                        activityInstanceId={activity.id}
                        activitySettings={activity.settings}
                        libraryName={activity.workspaceName ?? undefined}
                        onActivityFinished={handleFinishActivity}
                      />
                    )}
                  </ReportAnalyticsProvider>
                </div>
                <ActivityLiveEvent
                  isLive={liveListenerActive}
                  activityInstanceId={activity.id}
                  isRosteredRoom={activity.isRostered}
                  pubnub={window.pubnub}
                  roomName={room.nameLower}
                />
                {isFinishedActivity ? (
                  <ActivityResultFinished
                    showInsights={showInsights}
                    {...{
                      activity,
                      quiz,
                      quizQuestion,
                      quizAnswer,
                      responses,
                      students,
                      room,
                    }}
                  />
                ) : (
                  <ActivityResultLive
                    {...{
                      activity,
                      quiz,
                      quizQuestion,
                      quizAnswer,
                      responses,
                      students,
                      room,
                    }}
                  />
                )}
              </div>
            </AsyncDialogContext>
          </InviteStudentDrawerContext>
        </StudentSortProvider>
      </ActivityResultProvider>
    );
  }

  // Default to loading state
  return <ActivityResultLoading />;
}
